import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Row, Col, Button, Spinner } from 'react-bootstrap';
import AuditPreparationModal from '../../../common/AuditPreparationModal';
import AuditDocumentsMenu from './AuditDocumentsMenu';
import './AuditOverview.css';

const AuditOverview = ({ 
    dataroomId, 
    token, 
    workflowData, 
    auditData,
    preparingAudit,
    prepareError,
    prepareAudit,
    preparationStatus,
    onRefetch,
    onFileOpen 
    }) => {
    const { t } = useTranslation();
    const [showDetails, setShowDetails] = useState(false);
    const [showPreparationModal, setShowPreparationModal] = useState(false);

    const toggleDetails = () => setShowDetails(!showDetails);

    const handlePrepareAudit = async () => {
        setShowPreparationModal(true);
        try {
            await prepareAudit(); 
            onRefetch();
        } catch (error) {
            console.error('Error preparing audit:', error);
        }
    };

    const handlePreparationComplete = () => {
        setShowPreparationModal(false);
        onRefetch();
    };

    const formatKey = (key) => {
        return key
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Calculate workflow statistics
    const totalControls = workflowData?.reduce((sum, cycle) => sum + cycle.controls.length, 0) || 0;
    const completedControls = workflowData?.reduce((sum, cycle) => 
        sum + cycle.controls.filter(control => control.status === "Completed").length, 0) || 0;
    const completionPercentage = totalControls > 0 ? Math.round((completedControls / totalControls) * 100) : 0;

    // Format workflow data to match other sections
    const workflowStats = {
        [t('pages.workflow.totalControls')]: totalControls,
        [t('pages.workflow.completedControls')]: completedControls,
        [t('pages.workflow.completionPercentage')]: `${completionPercentage}%`,
        [t('pages.workflow.totalCycles')]: workflowData?.length || 0
    };

    if (prepareError) return (
        <Card className="audit-overview-card mb-4">
            <Card.Body>
                <div className="alert alert-danger">
                    {`${t("pages.workflow.errorPreparingAudit")} ${prepareError}`}
                </div>
            </Card.Body>
        </Card>
    );

    if (!auditData) return null;

    return (
        <>
            <Card className="audit-overview-card mb-4">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <Card.Title className="audit-overview-title">
                            {auditData.client_name} - {t("general.fiscalYear")} {auditData.fiscal_year}
                        </Card.Title>
                        <button
                            onClick={toggleDetails}
                            className="details-toggle-btn"
                        >
                            {showDetails ? t("general.hideDetails") : t("general.showDetails")}
                            <i className={`bi ${showDetails ? 'bi-chevron-up' : 'bi-chevron-down'} ms-1`}></i>
                        </button>
                    </div>
                    <Row className="audit-overview-info">
                        <Col md={6}>
                            <p>
                                <i className="bi bi-calendar-event icon"></i>
                                <strong>{t("general.createdOn")}</strong>
                                <span>{new Date(auditData.created_at).toLocaleDateString()}</span>
                            </p>
                            <p>
                                <i className="bi bi-person-badge icon"></i>
                                <strong>{t("general.auditor")}</strong>
                                <span>{auditData.global_auditor_name}</span>
                            </p>
                        </Col>
                        <Col md={6}>
                            <p>
                                <i className="bi bi-building icon"></i>
                                <strong>{t("general.client")}</strong>
                                <span>{auditData.client_name}</span>
                            </p>
                            <p>
                                <i className="bi bi-briefcase icon"></i>
                                <strong>{t("general.activity")}</strong>
                                <span>{auditData.activity_type}</span>
                            </p>
                        </Col>
                    </Row>
                    
                    <div className={`audit-details ${showDetails ? 'show' : ''}`}>
                        <Card.Title className="mt-4">{t("general.riskAssessment")}</Card.Title>
                        <Row className="audit-risk-assessment mb-3">
                            {Object.entries(auditData.global_risks || {}).map(([key, value]) => (
                                <Col md={6} key={key}>
                                    <p>
                                        <strong>{formatKey(key)}</strong>
                                        <span>{value}</span>
                                    </p>
                                </Col>
                            ))}
                        </Row>
                        
                        <Card.Title>{t("general.financialHighlights")}</Card.Title>
                        <Row className="audit-financial-highlights mb-3">
                            {Object.entries(auditData.financial_highlights || {}).map(([key, value]) => (
                                <Col md={6} key={key}>
                                    <p>
                                        <strong>{formatKey(key)}</strong>
                                        <span>{value}</span>
                                    </p>
                                </Col>
                            ))}
                        </Row>
                        
                        <Card.Title>{t("general.auditThresholds")}</Card.Title>
                        <Row className="audit-thresholds mb-3">
                            {Object.entries(auditData.thresholds || {}).map(([key, value]) => (
                                <Col md={6} key={key}>
                                    <p>
                                        <strong>{formatKey(key)}</strong>
                                        <span>{value}</span>
                                    </p>
                                </Col>
                            ))}
                        </Row>

                        <Card.Title>{t("general.additionalInformation")}</Card.Title>
                        <Row className="additional-info mb-3">
                            {Object.entries(auditData.other_information || {}).map(([key, value]) => (
                                <Col md={6} key={key}>
                                    <p>
                                        <strong>{formatKey(key)}</strong>
                                        <span>{value}</span>
                                    </p>
                                </Col>
                            ))}
                        </Row>

                        <Card.Title>{t("general.workflowProgress")}</Card.Title>
                        <Row className="workflow-progress mb-3">
                            {Object.entries(workflowStats).map(([key, value]) => (
                                <Col md={6} key={key}>
                                    <p>
                                        <strong>{formatKey(key)}</strong>
                                        <span>{value}</span>
                                    </p>
                                </Col>
                            ))}
                        </Row>
                    </div>
                    
                    <div className="mt-2 d-flex flex-wrap gap-2">
                    <AuditDocumentsMenu 
                        dataroomId={dataroomId}
                        token={token}
                        onFileOpen={onFileOpen}
                        onRefetchAudit={onRefetch}
                        documents={{
                            // Audit Planning Documents
                            auditPlanUrl: auditData.audit_plan_presigned_url,
                            engagementLetterUrl: auditData.engagement_letter_presigned_url,
                            
                            fecUrl: auditData.fec_presigned_url,
                            generalLedgerUrl: auditData.general_ledger_presigned_url,
                            balanceSheetUrl: auditData.balance_sheet_presigned_url,
                            subsidiaryLedgersUrl: auditData.subsidiary_ledgers_presigned_url,
                            incomeStatementUrl: auditData.income_statement_presigned_url
                        }}
                    />
                        <Button
                            variant="success"
                            onClick={handlePrepareAudit}
                            disabled={preparingAudit}
                        >
                            {preparingAudit ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    {t('pages.workflow.preparing')}
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-robot me-2"></i>{t("pages.workflow.prepareAudit")}
                                </>
                            )}
                        </Button>
                        <Button 
                            variant="success" 
                            onClick={() => {}} 
                            disabled
                        >
                            <i className="bi bi-file-earmark-text me-2"></i>{t("pages.workflow.generateAuditReport")}
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            <AuditPreparationModal
                show={showPreparationModal}
                preparationStatus={preparationStatus}
                onHide={() => {
                    if (!preparingAudit) {
                        setShowPreparationModal(false);
                        onRefetch();
                    }
                }}
                onComplete={handlePreparationComplete}
            />
        </>
    );
};

export default AuditOverview;